﻿import { handleResponse } from './responseHandler';
import { sso } from '../../auth/sso';

// The `scope` arg is now an inert no-op (a single Stytch session token serves
// every resource server); only its presence marks a call authenticated. Token comes from
// the internal-SSO session store; a null token → re-authorize at the auth site.
async function authorizedFetch(resourceURL, options, scope) {
  const accessToken = await sso.getToken();
  if (!accessToken) {
    // No token: ask the guard where to go rather than assuming "log in". During a
    // sign-out the token is gone BY DESIGN and a redirect is already in flight, so
    // minting an authorize url here would supersede it and silently restore the
    // session. The guard returns the logout url in that case and the authorize url
    // otherwise, so one call covers both without this adapter having to tell them
    // apart — and, critically, without a second read of the sign-out latch, which is
    // consuming: reading it here would take it from the route guard.
    const result = await sso.checkAuthenticated(window.location.href);
    if (!result.authenticated) {
      window.location.assign(result.redirectUrl);
    }
    // Redirecting away — return a never-resolving promise so callers don't act on a
    // half-built request during navigation.
    return new Promise(() => {});
  }

  const response = await fetch(resourceURL, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
  });

  // Mirrors api3.js's axios response interceptor: a 401 here means the session
  // was revoked server-side (not merely "no token"), so hand it to
  // sso.onUnauthorized() for the silent-refresh / bounded-streak re-auth policy.
  // Only the status is read — the body is left untouched for handleResponse below.
  //
  // Deliberately NOT gated on a sign-out check: sso.onUnauthorized() reads the latch
  // itself before doing anything. A check here would consume it first and leave the
  // route guard to treat a sign-out as an ordinary sessionless load.
  if (response.status === 401) {
    await sso.onUnauthorized();
  }

  return response;
}

async function fetchHandler(resourceURL, options, scopes) {
  const authorized = scopes !== undefined ? true : false;
  if (authorized) {
    return await authorizedFetch(resourceURL, options, scopes);
  }
  return await fetch(resourceURL, options);
}

function kebabCaseToCamel(str) {
  return str.replace(/(-\w)/g, (matches) => matches[1].toUpperCase());
}

class API {
  constructor({ url, scopes }) {
    this.url = url;
    this.endpoints = {};
    this.scopes = scopes;
  }

  /**
   * Create and store a single entity's endpoints
   * @param {A entity Object} entity
   */
  createEntity(entity) {
    /**
     * If there is a - in the entity.name, then change it
     * to camelCase. E.g
     * ```
     * myApi.createEntity({ name : 'foo-bar'})
     * myApi.endpoints.fooBar.getAll(...)
     */

    const name = kebabCaseToCamel(entity.name);
    this.endpoints[name] = this.createBasicCRUDEndpoints(entity.route ? entity.route : entity.name);
  }

  createEntities(arrayOfEntity) {
    arrayOfEntity.forEach(this.createEntity.bind(this));
  }

  /**
   * Create the basic endpoints handlers for CRUD operations
   * @param {A entity Object} entity
   */
  createBasicCRUDEndpoints(name, params, url, textResponse = false) {
    const endpoints = {};

    let resourceURL = `${this.url}/${name}`;
    if (url) {
      resourceURL = `${url}/${name}`;
    }

    if (params) {
      resourceURL = `${resourceURL}?${params}`;
    }

    endpoints.get =
      (
        options = {
          crossDomain: true,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      ) =>
      async () => {
        const response = await fetchHandler(resourceURL, options, this.scopes);
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
        return textResponse === true ? response.text() : response?.json();
      };

    endpoints.getWithURI =
      (
        uri,
        options = {
          crossDomain: true,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      ) =>
      async () => {
        const fullURL = `${resourceURL}/${uri}`; // Append the URI to the resourceURL
        const response = await fetchHandler(fullURL, options, this.scopes);
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          const error = new Error(`Failed to fetch: ${fullURL}`);
          error.status = response.status;
          throw error;
        }
        return textResponse === true ? response.text() : response?.json();
      };

    endpoints.getMultipleQueryString =
      (
        queries,
        options = {
          crossDomain: true,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }
      ) =>
      async () => {
        const response = await fetchHandler(
          `${resourceURL}?${queries
            ?.map((currentValue, index, array) => {
              if (array.length - 1 === index) {
                return `${currentValue.queryParam}=${currentValue.queryVal}`;
              }
              return `${currentValue.queryParam}=${currentValue.queryVal}&`;
            })
            .join('')}`,
          options,
          this.scopes
        );
        const contentType = response.headers.get('content-type');
        return !contentType.includes('application/json') || response.json();
      };

    endpoints.post =
      (
        body,
        options = {
          crossDomain: true,
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
        queries
      ) =>
      async () => {
        const response = await fetchHandler(
          `${resourceURL}${getQueryString(queries)}`,
          options,
          this.scopes
        );
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
        return textResponse === true ? response.text() : response?.json();
      };

    endpoints.postData =
      (
        data,
        queries,
        options = {
          crossDomain: true,
          method: 'POST',
          body: data,
        }
      ) =>
      async () => {
        const response = await fetchHandler(
          `${resourceURL}?${queries
            ?.map((currentValue, index, array) => {
              if (array.length - 1 === index) {
                return `${currentValue.queryParam}=${currentValue.queryVal}`;
              }
              return `${currentValue.queryParam}=${currentValue.queryVal}&`;
            })
            .join('')}`,
          options,
          this.scopes
        );
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
        return response.json();
      };

    endpoints.patch =
      (
        data,
        queries,
        options = {
          crossDomain: true,
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json', // Set Content-Type header to application/json
          },
          body: JSON.stringify(data),
        }
      ) =>
      async () => {
        const response = await fetchHandler(
          `${resourceURL}${getQueryString(queries)}`,
          options,
          this.scopes
        );
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
        return response.json();
      };

    endpoints.put =
      (
        data,
        options = {
          crossDomain: true,
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      ) =>
      async () => {
        const response = await fetchHandler(resourceURL, options, this.scopes);
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
        return response.json();
      };

    endpoints.putWithURI = (uri, body) => async () => {
      const fullURL = `${resourceURL}/${uri}`;
      const options = {
        crossDomain: true,
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      };
      const response = await fetchHandler(fullURL, options, this.scopes);
      const responseOK = await handleResponse(response);
      if (!responseOK) {
        throw new Error(`Failed to fetch: ${fullURL}`);
      }
      return response.json();
    };

    endpoints.delete =
      (
        id,
        options = {
          crossDomain: true,
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      ) =>
      async () => {
        const response = await fetchHandler(resourceURL, options, this.scopes);
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
      };

    endpoints.deleteWithURI = (
      pathParams = {},
      options = {
        crossDomain: true,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    ) => {
      let resourceURLWithParams = resourceURL;
      if (Object.keys(pathParams).length > 0) {
        const path = Object.values(pathParams).map(encodeURIComponent).join('/');
        resourceURLWithParams += `/${path}`;
      }
      return async () => {
        const response = await fetchHandler(resourceURLWithParams, options, this.scopes);
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURLWithParams}`);
        }
      };
    };

    endpoints.postMultipleQueryString =
      (
        queries,
        body,
        options = {
          crossDomain: true,
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      ) =>
      async () => {
        const response = await fetchHandler(
          `${resourceURL}?${queries
            .map((currentValue, index, array) => {
              if (array.length - 1 === index) {
                return `${currentValue.queryParam}=${currentValue.queryVal}`;
              }
              return `${currentValue.queryParam}=${currentValue.queryVal}&`;
            })
            .join('')}`,
          options,
          this.scopes
        );
        const responseOK = await handleResponse(response);
        if (!responseOK) {
          throw new Error(`Failed to fetch: ${resourceURL}`);
        }
        return response.json();
      };

    return endpoints;
  }
}

const getQueryString = (queries) => {
  if (!queries) {
    return '';
  }
  return `?${queries
    ?.map((currentValue, index, array) => {
      if (array.length - 1 === index) {
        return `${currentValue.queryParam}=${currentValue.queryVal}`;
      }
      return `${currentValue.queryParam}=${currentValue.queryVal}&`;
    })
    .join('')}`;
};

export default API;
